import { useEffect, useRef, useState } from "react";
import type { PreflightVoice } from "../../hooks/usePreflightVoice";
import { extractName, rankNameCandidates, type NameVerdict } from "../../api/structureClient";
import { transcribeNameAudio } from "../../api/nameSecondAsr";
import { encodeWavPcm16 } from "../../audio/wavEncoder";
import { formatName, hasKanji } from "../../lib/nameNormalize";
import { matchKey, describeSpelling } from "../../lib/kanaKey";
import {
  buildNameCandidates,
  MAX_CANDIDATES,
  type CandidateSource,
  type NameCandidate,
} from "../../lib/nameCandidates";
import { TTS_PHRASES } from "../../audio/ttsPhrases";
import { mediaStreamManager } from "../../audio/mediaStreamManager";
import { useProcessingCue } from "../../hooks/useProcessingCue";

// 氏名確認 (spec 2026-07-19 §4.1 + カタカナ表示 D23)。マイクは許可 step 以降 on のまま
// (mediaStreamManager singleton) なので、表示と同時に案内を読み上げながら
// 自動でリスニングを開始する (spec D12: 押下操作なし / D18: 読み上げ中も割り込み可)。
// 音声のみ (spec D11: テキスト入力 fallback なし)。氏名は keyterms が効かない
// 未知固有名詞で誤認識しやすいため復唱確認を必須とし、修正は「いいえ」→ 再録で行う。
//
// D23: 表示・保存・復唱はすべてカタカナのみ (formatName 参照)。漢字は読み方が確定しない
// 限り出さない (中韓読みの再発と姓の読み違いによる不快感を入口で塞ぐ)。
// 読み上げ文言は ttsPhrases.ts に集約 (事前生成の単一ソース化、2026-07-26)。

// 音では決まらない表記の差を選んでもらう画面の見出し (spec 2026-07-26 §6 / D25-4)。
// **読み上げないので ttsPhrases.ts には置かない** (あちらは読み上げ文言の単一ソースで、
// 事前生成の数え上げの起点。画面だけの文字列を混ぜると生成対象が水増しされる)。
const SPELLING_PROMPT_TEXT = "お名前の書き方をお選びください。";

// 言い直しの最中に「さっきは何と聞こえたか」を見せる文言。**読み上げないので
// ttsPhrases.ts には置かない** (SPELLING_PROMPT_TEXT と同じ扱い)。声だけの経路は
// 従来と 1 ビットも変わらない — 画面を見ている人への補助情報に閉じてある。
const HEARD_TITLE_TEXT = "先ほどの聞き取り";
const HEARD_HINT_TEXT = "違うところを、少しゆっくりお話しください。";

// 音では決まらない表記の差を決めてもらう問いかけ (D25-4)。読み上げでは差が伝わらないので、
// describeSpelling が説明できるときだけ言い添える (説明できない長音の書き分けは、
// 口頭で「『おう』ですか、『おお』ですか」と尋ねても伝わらないので素の復唱にする)。
function spellingQuestion(first: string, description: string | null): string {
  const hint = description ? `${description}のほう、` : "";
  return `お名前の書き方が 2 通りあります。${hint}「${first}」でよろしいですか?`;
}

// 復唱確認の問いかけ (spec 2026-07-26 §6)。**画面と読み上げで同じ文字列を使う**
// (この step の従来からの作法 — 聞き逃した人が画面で同じ問いを読める)。
// followUp = 同じ発話から出た別の候補を続けて出している場合 (逐次確認、D25-5)。
// 「先ほどの」を付けるのは、素の復唱のままだと「もう一度お名前をお話しください」と
// 言われたと誤解され、本人が答え直してしまうため。
function confirmQuestion(display: string, followUp: boolean): string {
  return followUp
    ? `先ほどの「${display}」さんでしょうか?`
    : `「${display}」さんですね。よろしいですか?`;
}

// 収束しないと判定した理由 (spec 2026-07-26 D25-3 / D25-7、計測に載せる)。
export type NameEscalationReason = "same-result" | "no-progress" | "max-attempts";

// 1 回の認識から得た表記は `NameCandidate` (src/lib/nameCandidates.ts) が持つ。
// **組み立てごと純関数へ移した (spec 2026-08-20 §3.4)** — 供給元の並びと、denied を
// 落としてから上限で切る順序 (D18) をコンポーネントの外に閉じ込めると、順序を壊す変異が
// 純関数の単体テストで殺せる。ここが持つのは「積む / 突き合わせる / 提示する」だけ。
//
// `source` の union は "ranked" が増えたが、この段では `ranked` に常に空を渡すので
// 出てこない (計測で読み始めるのは captureInfo の candidateSource から)。

// 認識失敗の自動再リスニング上限 (STT/回線障害で WSS 接続を無限ループさせない
// ための安全弁、spec D12)。**askYesNo の曖昧リトライとは別カウンタ**。
const MAX_CONSECUTIVE_FAILURES = 3;
// 復唱確認で曖昧な応答を聞き直す上限。上記とは独立 (Global Constraints)。
const MAX_UNCLEAR = 2;
// 氏名抽出 LLM (extractName) の timeout。素の fetch は timeout を持たないため、ハング
// (resolve も reject もしない) でも有限時間で打ち切って静的 fallback に倒す — preflight の
// クリティカルパス await は必ず有限時間で決着させる規約 (usePreflightVoice の
// SYNTHESIS_TIMEOUT_MS と同じ、D22)。兄弟定数と揃えてモジュールスコープに置く。
const NAME_EXTRACTION_TIMEOUT_MS = 5000;

// 第 2 経路 (ElevenLabs batch STT) と順位付け (LLM) の予算 (spec 2026-08-20 D10 / §4.2)。
//
// **この 2 つの数と「待ち側の締切の基準点」(D23) は、どれも単独では動かせない。**
//   順位付けの発火 F = max(S, 第 2 経路の決着) ≤ max(S, 2000)   (S = Scribe の確定)
//   順位付けの決着   ≤ F + 3000               ≤ max(S, 2000) + 3000
//   待ち側の締切     = S + NAME_EXTRACTION_TIMEOUT_MS
//   (a) 働いている推論を切らない: S ≥ 2000 なら余裕 2000ms / S < 2000 なら余裕 S
//   (b) resolving の最悪値は max(S + 5000, S + 5000) = S + 5000 = **現在と同一**
// 基準点を onSpeechEnd (t=0) に置くと (b) は成り立つが (a) が破れ (遅い回だけ順位付けが
// 黙って切られる)、待ちを「await 開始からの相対 3000ms」にすると (a) は成り立つが
// (b) が S + 8000 に伸びる。3 つ揃って初めて「機能を切らず、待ち時間も増やさない」になる。
const SECOND_ASR_TIMEOUT_MS = 2000;
const RANKING_TIMEOUT_MS = 3000;
// withTimeout は timeout 側が勝つと reject するだけなので、**どちらの理由で失敗したか**は
// メッセージで見分ける (timeout と failed を混ぜると、系統的な contract 違反が
// 「遅かった」に見える、D27)。
const SECOND_ASR_TIMEOUT_MESSAGE = "name-second-asr-timeout";
const RANKING_TIMEOUT_MESSAGE = "name-rank-timeout";
const RANK_DEADLINE_MESSAGE = "name-rank-deadline";

// 非氏名判定の連続上限 (spec 2026-07-25 D24-3)。ここに達したらこの step の間は判定を
// 降ろす (fail-open)。珍しい姓ほど LLM が誤って unrelated を返しやすく、判定器が同じ人に
// 2 回外した以上は信用しない、という立場。一方通行なので判定起因の無限ループが起きない。
// **既存の MAX_CONSECUTIVE_FAILURES (STT 失敗) / MAX_UNCLEAR (yes-no 曖昧) とは別カウンタ。**
const MAX_REJECTIONS = 2;

// 認識成功の通算上限 (spec 2026-07-26 D25-7)。参照 PoC の MAX_TURNS と同じ「相手の時間を
// 無限に使わない」ための最後の網。
// **既存の MAX_CONSECUTIVE_FAILURES (STT 失敗) / MAX_UNCLEAR (yes-no 曖昧) /
// MAX_REJECTIONS (非氏名判定) とは別カウンタ。** 数えている対象が違う。
//
// **4 → 6 に上げた (spec 2026-08-19 D6)。これは D2 が強制する変更である。**
// recognitionsRef は候補を集めた後に加算されるので、partial を却下していた頃は
// 数えられなかった。受理すると数えられるようになり、上限に早く届く。
// パイロットで最も苦労したセッション (c605b409、5 回言い直して確定) を追うと、
// **4 のままでは 4 回目で退避し、5 回目の正解に到達しない** — キーボードは最後の手段
// という前提と正面から衝突する。1 セッション内で観測された最大の言い直しが 5 回なので
// 6 にした (5 回目で確定でき、6 回目でなお決まらなければ退避する)。
// **上限そのものを外してはいけない** — partial が続くかぎり recognitionsRef は増え、
// D2 で rejectionsRef の fail-open が partial を数えなくなったぶん、こちらが最後の網になる。
const MAX_RECOGNITIONS = 6;

// 却下する verdict (spec 2026-08-19 D2)。**partial は含まない** — 「姓が取れた」ではなく
// 「氏名の形が揃っていない」という信号にすぎず、実測では認識できた側が正解の一部だったのは
// 11 件中 5 件だけだった。却下せず復唱確認に載せ、本人に「はい / いいえ」で決めてもらう。
const REJECTED_VERDICTS = ["unrelated", "refusal"] as const;
type RejectedVerdict = (typeof REJECTED_VERDICTS)[number];
const isRejected = (v: NameVerdict | null): v is RejectedVerdict =>
  v !== null && (REJECTED_VERDICTS as readonly string[]).includes(v);

// verdict 別の再入力案内 (spec 2026-07-25 §6)。読み上げと画面表示に同じ文字列を使う。
// 1 回目も 2 回目も同じ文言を読む (既存 registerFailure の FAILED_TEXT と同じ作法)。
//
// **型を Exclude<NameVerdict, "name"> から狭めた (spec 2026-08-19 D5)。** 受理条件の
// 条件式だけを直すと、後から REJECTION_TEXT[verdict] を触った人が partial を却下側へ
// 戻せてしまう。**partial を却下できないことを型で保証する。**
// TTS_PHRASES.nameRejectPartial と事前生成音声は残してあるが、どこからも参照しない
// (D4 — 満名を partial とラベルする回があるので、フルネームを既に言った人に
// 「フルネームでお願いします」と言うことになる)。
const REJECTION_TEXT: Record<RejectedVerdict, string> = {
  unrelated: TTS_PHRASES.nameRejectUnrelated,
  refusal: TTS_PHRASES.nameRejectRefusal,
};

// promise を有限時間で決着させる。timeout 側が勝ったら reject し、どちらが勝っても
// タイマーを片付ける (usePreflightVoice の synthesizeWithTimeout と同じ後始末)。
function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e: unknown) => {
        clearTimeout(timer);
        reject(e);
      }
    );
  });
}

const isTimeoutError = (e: unknown, message: string): boolean =>
  e instanceof Error && e.message === message;

// 第 2 経路 / 順位付けの結末 (spec 2026-08-20 §5.2 / §5.3、D27)。
// **`aborted` を分けるのが要点** — `failed` に混ぜると「上流が壊れている」と
// 「こちらが打ち切った」が区別できず、**課金したのに捨てた回**も数えられない。
//
// **第 2 経路では `"empty"` に到達しない (4 値だけ)。** `transcribeNameAudio` が
// 「200 だが text が空」と「HTTP / 回線 / 非 JSON の失敗」を**どちらも null に畳む**ため
// (`src/api/nameSecondAsr.ts`)。無音の発話で上流が空文字を返すのは実際に起きる形なので、
// **系統的な `failed` を見たときに「中継が壊れている」と切り分けられない**。
// 順位付け側 (`name_ranked.outcome`) は 5 値すべてに到達する。
export type NameEnrichOutcome = "ok" | "empty" | "failed" | "timeout" | "aborted";

// 第 2 経路の結果 (計測、§5.2)。text は outcome === "ok" のときだけ載せる。
export type NameSecondAsrInfo = {
  index: number;
  ms: number;
  outcome: NameEnrichOutcome;
  text?: string;
};

// 順位付けの結果 (計測、§5.3)。**撃った回は失敗でも必ず運ぶ (D27)** — 無いと
// 系統的な 400 が「LLM が候補を出せなかった」と区別できない。
export type NameRankedInfo = {
  index: number;
  ms: number;
  // 渡した書き起こしの本数 (1 か 2)
  paths: number;
  // 検証後に残った件数 (0〜3)
  returned: number;
  // 検証後の配列 (生の綴り)
  ranked: string[];
  // 上位 2 件のうち、渡した書き起こしから直に読める鍵ではないものの件数
  novel: number;
  outcome: NameEnrichOutcome;
};

// 1 サイクルぶんの第 2 経路 + 順位付け (spec 2026-08-20 §3.5)。**ref ではなく runCycle の
// ローカル変数として持つ (D20)** — handleNo / handleManualRetry は世代を進めてその場で
// 新しい runCycle を起こす一方、古いチェーンは自分の次の isStale 地点まで生きている。
// 単一スロットの ref を共有すると**古いチェーンの abort() が新しいサイクルの enrichment を
// 殺す**。ローカルなら世代タグも一致判定も要らない。
type NameEnrichment = {
  // onNameAudio が返したその回の通番 (§5.1)。新 2 イベントはこの番号で emit する。
  index: number;
  // Scribe の確定文。**順位付けの発火はここ**で、待ち側の締切の基準点もここで確定する。
  setScribeText: (text: string) => void;
  // **決して reject せず、必ず有限時間で settle する** (§3.5 の settle 契約)。失敗は []。
  ranked: Promise<string[]>;
  // 第 2 経路の fetch を中断し、順位付けを発火させず、ranked を [] で解決する。
  abort: () => void;
  // 待ち側の締切 (D23)。Scribe の確定時刻 + NAME_EXTRACTION_TIMEOUT_MS。
  deadlineAt: () => number;
};

// 上位 2 件のうち、**渡した書き起こしから直に読める鍵ではない**ものの件数 (§5.3)。
// 候補一覧に無い読みを LLM が自分で作って当てた回が段 2 の実測で 3 件あり、この件数が
// 「B の強さ」に直接対応する。
//
// **読むときの注意 2 つ (どちらも実装後レビューで判明した)。**
// (1) **漢字だけの書き起こしでは構造的に上限 (2) に張り付く** — `known` は
// `matchKey(formatName(t, ""))` で作るので、`formatName` が漢字を落として空を返す回は
// `known` が空集合になる。実測: 「本村明子 / 本村明子です」→ known = {} → novel = 2。
// **つまり D7 / D26 が救いたい「漢字で返る回」でだけ最大値になる**ので、この数字は
// 「LLM が新しい読みを作った」ではなく「**書き起こしがカナで無かった**」と読むほうが近い。
// (2) **canon 空間の綴りの漏れ (D11) はここでは検知できない** — `matchKey` は長音・清濁の
// 書き分けをまさに畳むので (`matchKey("カトウシオリ") === matchKey("カトーシオリ")`)、
// 漏れても `novel` は 1 も増えない。**D11 の観測点は `name_ranked.ranked` の生綴りか
// `name_captured.displayName` 側にある** (spec §5.3 の当該記述は誤り)。
//
// **`display` (extractName の読み) とは突き合わせない** — emit は startEnrichment の中で
// 行う契約 (D27。await 側に置くと abort / timeout の回が 1 件も残らない) で、その時点では
// extractName がまだ決着していない (2 つは並行に走る)。後から `scribe.final` と
// `name_captured` を突き合わせれば厳密な「他の供給元が出していない鍵」は再現できる。
function countNovelReadings(ranked: string[], texts: string[]): number {
  const known = new Set(texts.map((t) => matchKey(formatName(t, ""))).filter((k) => k.length > 0));
  const novel = new Set<string>();
  for (const r of ranked.slice(0, MAX_CANDIDATES)) {
    const key = matchKey(formatName(r, ""));
    if (key.length > 0 && !known.has(key)) novel.add(key);
  }
  return novel.size;
}

type Props = {
  voice: PreflightVoice;
  // 位置引数ではなく info オブジェクトで運ぶ (値が 3 つになり、呼び出し側で
  // どれがどれか読めなくなるため)。
  onConfirmed: (
    name: string,
    info: {
      attempts: number;
      recognitions: number;
      // 判定器が partial を返した回数 (spec 2026-08-19 §5)。計測専用。
      partials: number;
      // 確定した復唱の読み上げにかかった時間 (2026-08-20)。復唱を経ない経路では省く。
      confirmNarratedMs?: number;
      // 確定した候補の出所 (spec 2026-08-20 §5.4)。**候補ではない経路 (キーボード) は省く。**
      // 「順位付けの 1 位がそのまま採用された回」をこの値の比率で読む。
      candidateSource?: CandidateSource;
      // その候補がこの回の fresh で何番目だったか (1 始まり)。**表記の 2 択は省く** —
      // 2 つが別サイクル由来なので 1 つの数では書けない。
      candidateRank?: number;
      method: "voice" | "keyboard";
    }
  ) => void;
  onRespond: (method: "voice" | "button" | "keyboard") => void;
  // 非氏名と判定して聞き直したときの通知 (計測、spec 2026-07-25 §7)。
  // consecutive は連続弾き回数で、2 に達した回のイベントが fail-open の作動を示す。
  // **却下する verdict だけを受ける (spec 2026-08-19 D5)。** partial は却下されなくなったので
  // ここには載らない (回数は onConfirmed の info.partials が運ぶ)。
  onRejected: (verdict: RejectedVerdict, attempt: number, consecutive: number) => void;
  // 収束しないと判定して退避画面へ落とした (spec 2026-07-26 D25-3 / D25-7)。計測用。
  onEscalated: (reason: NameEscalationReason, recognitions: number) => void;
  // 音では決まらない表記の差を 2 択で決めた (spec 2026-07-26 D25-4)。計測用。
  // described は差を口で言える形に説明できたか (ヅ/ズ は説明でき、長音の書き分けはできない)。
  onSpellingChoice: (
    chosen: "first" | "second",
    method: "voice" | "button",
    described: boolean
  ) => void;
  // 音では決まらない表記の差を「出した」瞬間の通知 (spec 2026-07-26 D25-4)。計測用。
  // onSpellingChoice (2 択が解決したときだけ発火) とは非対称にする — 「どちらでも
  // ありません」・曖昧応答の打ち切り・STT error の 3 経路では 2 択画面に落ちても
  // choice は出ず、そこから出る onEscalated("no-progress", …) は素の足踏み退避と
  // 区別がつかない。choice だけを見ると「2 択が発火しているのに 0 件」と誤読し
  // D25-4 自体が不要だったと判断されかねないため、提示した事実はこちらで残す。
  onSpellingPrompted: (described: boolean) => void;
  // 逐次確認 (spec 2026-07-26 D25-5) が実際に候補を提示した通知。計測用。spec §4.2 で
  // 「本リポでは退化する」と予告した枝なので、発火しないことを確認するためにこそ要る。
  // index はこの step で候補を提示した通算回数 (曖昧応答での聞き直しは同じ候補への同じ
  // 問いなので数えない)。1 人目から数えるので逐次確認で出る最小値は 2。録り直しをまたいで
  // 累積するため「この認識サイクルで何人目か」ではない点だけ注意。
  onFollowupPresented: (index: number) => void;
  // 氏名を聞き取るために回した発話の PCM (16 kHz mono、spec 2026-07-29 §4.4)。
  // **保存も通番も wizard 側 (Preflight) が持つ** — R2 のキーは session 単位なのに、
  // この component は入力デバイス切替や「← 戻る」で remount されるので、ここで番号を
  // 振ると remount のたびに 0 から振り直され、先に保存した音声が黙って上書きされる。
  // ここは「氏名の発話が 1 本取れた」ことだけを伝える (storage の知識を持ち込まない、原則 4)。
  //
  // **この prop は throw しない契約** (Preflight 側が握る)。throw すると
  // recognizeSingleUtterance の onSpeechEnd が途中で止まり、Scribe への commit ごと
  // 落ちて認識そのものが timeout する。
  //
  // **戻り値はその回の通番 (spec 2026-08-20 §5.1)。** 第 2 経路と順位付けのイベントは
  // 同じ番号で emit するので、この番号を返してもらう以外に対応付ける手段が無い
  // (R2 のキーは session 単位で、この component は remount されるため通番を自分で
  // 振ってはいけない)。**保存を見送った回 (sessionId が無い) も番号は返る** — 通番は
  // 既に進めてあるので、返さないと enrich が番号を失う。
  onNameAudio: (audio: Float32Array) => number;
  // 第 2 経路を撃った結果 (計測、spec 2026-08-20 §5.2)。**撃った回は失敗でも毎回呼ぶ。**
  onSecondAsr: (info: NameSecondAsrInfo) => void;
  // 順位付けを撃った結果 (計測、§5.3 / D27)。**撃たなかった回は呼ばない** —
  // リクエストを 1 度も送っていない回に paths: 0 の行を作ると「LLM に訊いたが
  // 0 件だった」と読み違える。
  onRanked: (info: NameRankedInfo) => void;
};

// listening = 発話待ち / hearing = 発話検出中 (声に反応、課題6/Approach A) /
// resolving = 発話終了後の確認中 (Scribe 確定 + LLM 抽出を前倒しで見せる) /
// confirming = 復唱確認 / spelling = 音では決まらない表記の差の 2 択 (D25-4) /
// retry-prompt = 3 回失敗後の手動リトライ
type CaptureState =
  | "listening"
  | "hearing"
  | "resolving"
  | "confirming"
  | "spelling"
  | "retry-prompt";

// 2 択に出す表記の組。first は先に提示した (過去に積んだ) 表記、second は今回出た表記。
// **文字列ではなく候補を丸ごと持つ (spec 2026-08-20 §5.4)** — 出所 (source) を落とすと、
// 2 択で確定した回が計測上「順位付けの寄与なし」に見える。
type SpellingChoice = { first: NameCandidate; second: NameCandidate; described: boolean };

export function NameCapture({
  voice,
  onConfirmed,
  onRespond,
  onRejected,
  onEscalated,
  onSpellingChoice,
  onSpellingPrompted,
  onFollowupPresented,
  onNameAudio,
  onSecondAsr,
  onRanked,
}: Props) {
  const [state, setState] = useState<CaptureState>("listening");
  // 案内を読み上げている最中か (合図音の抑止だけに使う内部フラグで、画面には出さない)。
  // resolving は「無音の処理待ち」と「聞き直し案内の読み上げ」の両方を含むため、
  // state だけでは合図音を鳴らしてよい区間を判定できない (下の useProcessingCue 参照)。
  const [narrating, setNarrating] = useState(false);
  const [candidate, setCandidate] = useState<string | null>(null);
  // いま出している候補が逐次確認の 2 人目以降か (D25-5)。画面の問いを読み上げと
  // 揃えるためだけに持つ。confirming に入るのは confirmLoop の先頭だけなので、
  // そこで candidate と同時に必ず書く (どちらか片方だけが古くなることはない)。
  const [candidateFollowUp, setCandidateFollowUp] = useState(false);
  // 復唱の読み上げが鳴り終わったか (2026-08-20)。**「はい」を押せるようにする条件**で、
  // これが false のあいだ「はい」は disabled になる。
  //
  // **なぜ要るのか**: `confirmLoop` は `setState("confirming")` を `askYesNo` より**前**に
  // 実行するので、確認カードと「はい」は読み上げが始まる前から画面にある。本番ログでは
  // **ボタンで確定した 12 件のうち 7 件が読み終わり (2.6 秒) より前**で、
  // 1.3 秒後に押した例もある = **自分の名前を言い終わる前に承認していた**
  // (計測 2026-08-20)。復唱確認が誤りを 6 件中 0 件しか止めていない主因はこれ。
  //
  // **「いいえ」は最初から押せる** — 誤った「はい」は取り返せないが、誤った「いいえ」は
  // 言い直しの 1 往復で済む。`interruptionJudge` と同じ非対称の判断で、
  // 弾く側 (先に進ませない側) には制限を掛けない。
  const [confirmNarrated, setConfirmNarrated] = useState(false);
  // 直近の復唱の読み上げにかかった時間 (計測用、`captureInfo` が載せる)。
  const confirmNarratedMsRef = useRef<number | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  // D21: 連続 3 回失敗後の retry-prompt でのみ出す本人テキスト入力 (最後の手段)。
  // 一次動線は音声のまま — listening / confirming 中は描画しない。
  const [showTextInput, setShowTextInput] = useState(false);
  const [textValue, setTextValue] = useState("");
  // D25-4: 音では決まらない表記の差の 2 択。**render (ボタンの描画) と、聴取ループから
  // 呼ばれる commitSpelling の両方から読む**ため state と ref を併走させる。ループは
  // 「2 択に入る前のレンダー」のクロージャで動いているので、state だけだと常に null を
  // 見てしまう (ref が唯一の最新値)。書き込みは必ず下の setSpellingChoice を通す。
  const [spellingChoice, setSpellingChoiceState] = useState<SpellingChoice | null>(null);
  const spellingChoiceRef = useRef<SpellingChoice | null>(null);
  const setSpellingChoice = (v: SpellingChoice | null) => {
    spellingChoiceRef.current = v;
    setSpellingChoiceState(v);
  };
  const attemptsRef = useRef(0);
  const failuresRef = useRef(0);
  // 連続の非氏名判定 (D24-3)。failuresRef (STT 失敗) とは独立で、こちらは
  // retry-prompt へは繋がらない (D24-6)。閾値到達で判定を降ろす。
  const rejectionsRef = useRef(0);
  // D25-1: この step で得た表記の蓄積。mount 単位で、handleManualRetry がリセットする
  // (rejectionsRef と同じ作法 — 本人が仕切り直しを選んだ以上こちらの記憶は捨てる)。
  const logRef = useRef<NameCandidate[]>([]);
  // 言い直しの最中に見せる「先ほどの聞き取り」(表示専用)。**判定用の logRef とは別物** —
  // 非氏名と判定して復唱に進まなかった回 (D24) もここには残す。名字だけを話すと
  // verdict:"partial" で候補を集める手前で早期 return するため、logRef を描いていた
  // 当初の実装では聞き取れているのに何も出なかった (実機で判明)。逆にその回を logRef へ
  // 積むと、収束不能の検出 (D25-3) や逐次確認 (D25-5) が「氏名として通っていない表記」を
  // 候補として扱い始めるので、判定側には混ぜない。
  //
  // **持つのは最新の 1 件だけで、履歴にはしない。** 実機で溜めてみると
  // 「アー、ト、トイウカ」「ウン、イナモトユウタ」のようなフィラー混じりの回まで並び、
  // 直したい表記が埋もれた (2026-08-04 のユーザー判断)。
  const [lastHeard, setLastHeard] = useState<string | null>(null);
  // 提示した候補が「その回の fresh で何番目だったか」(1 始まり、spec 2026-08-20 §5.4)。
  // **鍵で引ける形で持つ** — 「いいえ」で 2 人目に進む経路 (confirmLoop / handleNo) は
  // logRef から候補を引くので、候補そのものには順位が付いていない。logRef と同じ
  // mount 単位で、handleManualRetry がまとめて捨てる。
  const ranksRef = useRef<Map<string, number>>(new Map());
  // いま復唱している候補 (計測用、§5.4)。**ボタンの「はい」は画面の文字列 (candidate) しか
  // 持たない**ので、出所と順位をここから読む。confirmLoop が候補を切り替えるたびに
  // setCandidate と同じ場所で書くので、candidate が非 null のあいだ両者は必ず対応する。
  const presentedRef = useRef<NameCandidate | null>(null);
  // 「いいえ」で否定された鍵。同じものを二度提示しない (D25-5)。
  const deniedRef = useRef<Set<string>>(new Set());
  // 認識に成功した通算回数 (D25-7)。attemptsRef は非氏名判定や STT 失敗も含む通算値なので
  // 別に持つ (数えている対象が違う)。
  const recognitionsRef = useRef(0);
  // 判定器が partial を返した回数 (spec 2026-08-19 §5)。**計測専用で、挙動には使わない。**
  // partial を却下しなくなったので preflight.name_rejected には載らなくなる —
  // この数字が無いと「判定を直した後に姓のみが何件あったか」を後から数えられない
  // (rejections + partials で旧 rejections と同じ軸に戻せる)。
  // attempts / recognitions と同じ mount 単位のカウンタなので、ここに置く
  // (Preflight 側に持たせるとスコープを揃える effect が別途要る)。
  //
  // **handleManualRetry ではリセットしない。** 揃える相手は recognitions (仕切り直しで 0 に
  // 戻る) ではなく **rejections** — あちらは Preflight の ref で、リセット契機は
  // nameCaptureKey の effect だけである。rejections + partials を旧 rejections と同じ軸で
  // 比べるのが目的なので、リセット契機も揃えないと足し算が合わなくなる。
  const partialsRef = useRef(0);
  // この step で候補を提示した通算回数 (preflight.name_followup_presented.index の元、
  // D25-5)。confirmLoop が**候補を切り替えた回だけ**加算する — 曖昧応答 (unclear) での
  // 聞き直しは同じ候補への同じ問いなので数えない (2026-07-27 に修正。以前はループ先頭で
  // 毎周無条件に加算しており、聞き直しのたびに index が進んで event も再 emit されていた)。
  // 逐次確認で出すのは 2 人目以降なので index の最小値は 2 だが、録り直しをまたいで累積する
  // ため「この認識サイクルで何人目か」ではない。recognitionsRef 等と同じ
  // 「mount 単位、handleManualRetry でリセット」の扱い。
  const presentedCountRef = useRef(0);
  // 世代カウンタ。新しい聴取セッション (effect の mount / handleNo / handleManualRetry)
  // を始めるたびに 1 つ進め、その番号 (myGeneration) をセッションのクロージャに
  // 閉じ込める。各セッションの非同期処理は「今の世代が自分の世代と一致しているか」
  // (isStale) だけを見て継続可否を判断する。
  //
  // 実装注記 (brief 参照実装からの逸脱): brief は共有の boolean ref (doneRef) を
  // effect 冒頭で false にリセットする設計だったが、これは React 19 StrictMode の
  // mount→cleanup→remount で実際に二重ループを起こすことをテストで確認した——
  // 1 回目の mount の cleanup が doneRef.current=true にした直後、2 回目の mount が
  // 同じ ref を false に戻してしまう。1 回目の askText が abort 由来で reject する
  // のは (Promise の性質上) 必ずマイクロタスク以降に遅れるため、その catch が
  // doneRef.current を見た時点では既に 2 回目が false に戻した後になっており、
  // 1 回目は「自分はまだ現役だ」と誤認して registerFailure を実行し、さらに次の
  // サイクルの askText まで発行してしまう。これは 2 回目 (本物のセッション) の
  // 聴取を横から abort する実害のあるバグで、StrictMode テストで onConfirmed が
  // 2 回・誤った attempts 値で呼ばれる形で実際に再現した (詳細は task-5-report.md)。
  // 世代カウンタなら 1 回目が捕まえた番号は 2 回目が発行する新しい番号と二度と
  // 一致しないため、この誤認が起きない。
  const generationRef = useRef(0);
  // effect の依存に入れず ref 経由で最新値を読む (親の再レンダーで
  // 聴取が二重に走らないようにする)
  const onConfirmedRef = useRef(onConfirmed);
  onConfirmedRef.current = onConfirmed;
  const onRespondRef = useRef(onRespond);
  onRespondRef.current = onRespond;
  const onRejectedRef = useRef(onRejected);
  onRejectedRef.current = onRejected;
  const onEscalatedRef = useRef(onEscalated);
  onEscalatedRef.current = onEscalated;
  const onSpellingChoiceRef = useRef(onSpellingChoice);
  onSpellingChoiceRef.current = onSpellingChoice;
  const onSpellingPromptedRef = useRef(onSpellingPrompted);
  onSpellingPromptedRef.current = onSpellingPrompted;
  const onFollowupPresentedRef = useRef(onFollowupPresented);
  onFollowupPresentedRef.current = onFollowupPresented;
  const onNameAudioRef = useRef(onNameAudio);
  onNameAudioRef.current = onNameAudio;
  const onSecondAsrRef = useRef(onSecondAsr);
  onSecondAsrRef.current = onSecondAsr;
  const onRankedRef = useRef(onRanked);
  onRankedRef.current = onRanked;

  const isStale = (myGeneration: number) => generationRef.current !== myGeneration;

  // 処理中の合図音 (spec 2026-07-27 §3.3)。氏名の確認中 (Scribe の確定待ち + LLM 抽出) は
  // 初めての人が最初に出会う待ちなので、ここにも音を置く。VAD の持ち主は
  // recognizeSingleUtterance の内部にいて外から触れないため、track を無効にする方だけを使う
  // (VAD には無音が流れ、Silero は無音では発火しないので実質同じ結果になる)。氏名の音声は
  // resolving に入る前に commit 済みなので、認識結果には影響しない。
  //
  // narrating を除くのは spec §1.2 の再導入条件「音声と重ねない」を守るため。この SE は
  // 2026-07-25 に「音声が流れている時に鳴る SE で耳が痛い」で全廃したものの再導入で、
  // 「TTS も発話も無い無音区間だけで鳴る」が前提になっている。resolving のまま案内を
  // 読み上げる経路が 2 つある (D24 の非氏名判定と registerFailure の 1・2 回目) ので、
  // state だけを見ていると聞き直しのたびに声の上へ 850ms ごとに重なる。
  useProcessingCue({
    cueActive: state === "resolving" && !narrating,
    micActive: state === "resolving" && !narrating,
    suspendMic: () => mediaStreamManager.pause(),
    resumeMic: () => mediaStreamManager.resume(),
  });

  // resolving を保ったまま案内を読み上げる (spec 2026-07-25 §5.2 が新しい表示状態の追加も
  // listening への後戻りも禁じているので、表示状態は動かさず合図音だけ降ろす)。
  // narrating を false に戻すのは次サイクル冒頭の setState("listening") と同時 (runCycle)。
  // 読み上げ直後にここで戻すと、listening へ切り替わるまでの 1 レンダーだけ合図音が
  // 復活してマイクの停止・再開が無意味に往復する。
  const speakGuidance = async (text: string) => {
    setNarrating(true);
    await voice.speak(text);
  };

  // 収束しないと判定したときの退避 (D25-3 / D25-7)。既存の retry-prompt を
  // そのまま使う (「もう一度試す」とキーボード入力が既にある)。キーボードを
  // 案内した以上、入力欄はクリックさせずに開いておく。
  // 戻り値は runCycle の契約に合わせて常に true (このサイクルを終了する)。
  //
  // **画面への書き込みは await より前に閉じてある。** 読み上げを待っている間に
  // 「もう一度試す」が押されて世代が進んでも、遅れて再開した古いサイクルが
  // 仕切り直し後の画面へ退避表示を押し戻すことがないようにするため (世代カウンタが
  // 守っているのと同じ不変条件を、この関数は「書かない」ことで守る)。呼び出し側は
  // いずれも直前に isStale を確認しており、間に await を挟まない。
  const escalate = async (reason: NameEscalationReason): Promise<boolean> => {
    setState("retry-prompt");
    setCandidate(null);
    setSpellingChoice(null);
    setErrorMessage("");
    setShowTextInput(true);
    onEscalatedRef.current(reason, recognitionsRef.current);
    await voice.speak(TTS_PHRASES.nameEscalate);
    return true;
  };

  // 鍵は同じで表記だけが違う組を探す (D25-4)。log は鍵ごとに 1 件しか持たない
  // (runCycle の追記が既出の鍵を弾く) ので、鍵が一致する過去の表記は高々 1 つ。
  // 返す組は (先に積んだ表記, 今回出た表記) の順 — 先に提示した方を先に読み上げる。
  const findSpellingPair = (
    cands: NameCandidate[]
  ): { first: NameCandidate; second: NameCandidate } | null => {
    for (const c of cands) {
      const prior = logRef.current.find((a) => a.key === c.key);
      if (prior && prior.display !== c.display) return { first: prior, second: c };
    }
    return null;
  };

  // 2 択の確定。どちらを選んでも log / denied は触らず、その場で確定する。
  // 確定の権威は本人が選んだという行為そのものが持つ (spec §5.4)。
  // 進行中の聴取は「先に世代を進めてから cancel」で打ち切る (handleYes / handleNo と
  // 同じ cancel-first の作法 — 打ち切りが遅れると、その間に拾われた発話が
  // 本人の選択と違う方で確定してしまう)。
  const commitSpelling = (chosen: "first" | "second", method: "voice" | "button") => {
    const choice = spellingChoiceRef.current;
    if (!choice) return;
    generationRef.current += 1;
    voice.cancel();
    onSpellingChoiceRef.current(chosen, method, choice.described);
    onRespondRef.current(method);
    // 入力手段は音声のまま (キーボードで打ったわけではない)。どちらの表記かを
    // タップで選んだだけなので、name_captured.method は "voice" が正しい。
    const picked = chosen === "first" ? choice.first : choice.second;
    // **candidateRank は渡さない (§5.4)** — 2 つは別のサイクルで提示された表記なので
    // 「何番目」が 2 つの数に散っている。どちらを選んだかは name_spelling_choice が持つ。
    onConfirmedRef.current(
      picked.display,
      captureInfo("voice", { candidateSource: picked.source })
    );
  };

  // 2 択のどちらも違うときの逃げ道 (原則 5)。「いいえ」は 2 択では「もう一方」を意味する
  // ので、音声にもタップにも「どちらでもない」にあたる応答が無い。この画面に落ちるのは
  // 本人が一度「いいえ」と答えた後 (= 否定済みの表記が片方に並ぶ) なので、氏名そのものが
  // 誤って認識されていれば 2 つとも誤りになりうる。逃げ道を置かないと、D25-3 が開通させた
  // キーボードへの到達性 (spec §1.1 欠陥 1) をこの画面だけが塞ぎ返すことになる。
  // reason は "no-progress" — 出せる表記が尽きた足踏みそのものなので、計測の理由コードを
  // 増やさずに済む (schemaVersion / NameEscalationReason は据え置き)。
  const handleSpellingGiveUp = () => {
    generationRef.current += 1; // 進行中の 2 択の聴取を無効化する (cancel-first)
    voice.cancel();
    void escalate("no-progress");
  };

  // 音で決まらない差を、画面のタップと音声の両方で決める (D25-4)。
  // 2 択なので「いいえ」は「もう一方」を意味する。**タップを出すのは
  // 「音では決まらないと判定できた」場合に限られる**ので、voice-first (原則 5) の
  // 「音声を一次動線、タップは最後の手段」と整合する。
  //
  // 画面への書き込み (setSpellingChoice / setState) は escalate と同じく await より
  // 前に閉じてある。呼び出し側は直前に isStale を確認しており、間に await を挟まない。
  const resolveSpelling = async (
    first: NameCandidate,
    second: NameCandidate,
    myGeneration: number
  ): Promise<boolean> => {
    const description = describeSpelling(first.display, second.display);
    const described = description !== null;
    setSpellingChoice({ first, second, described });
    setState("spelling");
    // D25-4: 2 択を提示した事実そのものの計測。以降どう解決されるか (あるいは
    // 解決されずに退避へ抜けるか) に関わらず、ここで確定して 1 度だけ通知する。
    onSpellingPromptedRef.current(described);
    let unclear = 0;
    while (!isStale(myGeneration)) {
      let answer: "yes" | "no" | "unclear";
      try {
        answer = await voice.askYesNo(spellingQuestion(first.display, description));
      } catch {
        return true; // 認識不能 → ボタン待ちに倒す (原則 5)
      }
      if (isStale(myGeneration)) return true;
      if (answer === "yes" || answer === "no") {
        commitSpelling(answer === "yes" ? "first" : "second", "voice");
        return true;
      }
      unclear += 1;
      if (unclear >= MAX_UNCLEAR) {
        await voice.speak(TTS_PHRASES.useButton);
        return true; // 聴取を止めてボタン待ちにする (2 択は画面に残る)
      }
      await voice.speak(TTS_PHRASES.askAgain);
    }
    return true;
  };

  // 認識失敗と「空/無音」を同じ経路にまとめる。ユーザーからは「聞き取れなかった」のか
  // 「何も聞こえなかった」のか区別できないようにする (同じ文言 + 自動再リスニング)。
  // 戻り値: 自動再試行を続けてよいか (true = 続ける / false = 手動ボタン待ちへ)。
  const registerFailure = async (opts: { counted?: boolean } = {}): Promise<boolean> => {
    // **counted: 呼び出し側が既に attempts を数えている** (確定文が来て候補の組み立てまで
    // 進んだ枝)。ここで足すと 1 発話で 2 進む。他の 2 つの呼び出し点 (askText の catch /
    // 空の確定文) は attemptsRef の加算より上流なので引数なしのままでよい。
    if (!opts.counted) attemptsRef.current += 1;
    failuresRef.current += 1;
    setErrorMessage(TTS_PHRASES.nameFailed);
    if (failuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
      // ここは直前に resolving を抜けるので合図音は state 側で止まる (speakGuidance は不要)
      setState("retry-prompt");
      await voice.speak(TTS_PHRASES.nameGiveUp);
      return false; // 自動再試行を止め、手動ボタン待ちにする
    }
    await speakGuidance(TTS_PHRASES.nameFailed);
    return true;
  };

  // raw transcript → カタカナのみの表示名 (D23)。LLM 抽出 (extractName) を primary、失敗・timeout
  // 時は 静的 fallback (非ブロッキング、D22)。extractName は素の fetch で timeout を持たないため、
  // ハング (resolve も reject もしない) でも withTimeout で 5 秒で打ち切って fallback に倒す。
  // 空文字で返した場合は呼び出し側 (runCycle) が「カタカナに落とせない = 認識失敗」として
  // registerFailure に倒す。verdict は氏名かどうかの判定 (D24) で、null は「判定不能」。
  const resolveName = async (
    raw: string
  ): Promise<{ display: string; asrDisplay: string; verdict: NameVerdict | null }> => {
    try {
      const extracted = await withTimeout(
        extractName(raw),
        NAME_EXTRACTION_TIMEOUT_MS,
        "name-extraction-timeout"
      );
      // 語尾除去とカタカナ化は formatName が持つ (D25-8)。ここでは source を渡すだけ。
      return {
        display: formatName(extracted.name.trim() || raw, extracted.reading),
        // Scribe の生テキスト由来の表記 (§4.1)。**Scribe が仮名で返したときだけ**採る。
        // hasKanji の門を置くのは、stripKanji が漢字を落とすだけだから — 漢字かな混じり
        // (「今日はいい天気ですね」) だと残ったかな (「ハイイデスネ」) が候補として積まれて
        // しまう。「漢字なら空」という §4.1 の契約を字義どおりに実装する。
        asrDisplay: hasKanji(raw) ? "" : formatName(raw, ""),
        verdict: extracted.verdict,
      };
    } catch {
      // LLM 失敗/timeout は「判定しない」に倒す (D24-5)。
      // display は既に asr 由来なので、2 つ目は持たない。
      return { display: formatName(raw, ""), asrDisplay: "", verdict: null };
    }
  };

  // onConfirmed に載せる計測値。**4 つの確定経路 (復唱 / 2 択 / ボタン / キーボード) で
  // 同じ形を使う** — 散らすと partials のようなフィールドを足したときに必ずどれかが漏れる。
  // confirmNarratedMs は**復唱を経て確定した経路だけが渡す** (2026-08-20)。ref を
  // ここで直に読まないのは、それだと否定された復唱の値が残ったままキーボード確定に
  // 貼り付くため (「確定した復唱の読み上げ時間」という意味が壊れる)。
  // どの経路が渡すかは呼び出し側の責任だが、**フィールドの一覧はここ 1 箇所に保つ**。
  //
  // **引数は options object にする (spec 2026-08-20 §5.4)。** 位置引数のままだと段 3 で
  // 2 つ増えて 4 つになり、呼び出し側で「3 番目は何だったか」が読めなくなる。
  const captureInfo = (
    method: "voice" | "keyboard",
    extra: {
      confirmNarratedMs?: number;
      candidateSource?: CandidateSource;
      candidateRank?: number;
    } = {}
  ) => ({
    attempts: attemptsRef.current,
    recognitions: recognitionsRef.current,
    partials: partialsRef.current,
    // 本人を待たせた時間。**「はい」を読み終わりまで押せなくしたことの代償**がこれで測れる。
    confirmNarratedMs: extra.confirmNarratedMs,
    // 確定した候補の出所と、その回の fresh で何番目だったか (§5.4)。
    candidateSource: extra.candidateSource,
    candidateRank: extra.candidateRank,
    method,
  });

  // まだ本人に見せていない候補 (D25-5)。**提示した候補は確定か否定のどちらかに必ず
  // 落ちる**ので (曖昧応答や STT 失敗で聴取が止まってもボタンの はい/いいえ しか
  // 出口が無い)、「denied でない」= 「まだ提示していない」と一致する (spec §5.2)。
  const nextUnpresented = (): NameCandidate | undefined =>
    logRef.current.find((a) => !deniedRef.current.has(a.key));

  // 復唱確認のループ。true = このサイクル終了 (確定 / 退避 / ボタン待ち) /
  // false = 録り直しへ。「いいえ」を受けたら、まだ本人に見せていない候補が積まれて
  // いればそれを続けて確かめる (逐次確認、D25-5)。これが無いと、一度も見せていない
  // 候補を log に残したまま退避してしまう — それが起きるのは Scribe が仮名で返した回
  // (1 発話から候補が 2 つ取れた回、§4.1) で、未知の固有名詞や外国人名という
  // **この機能が最も助けたい層**にあたる。
  //
  // followUp は「呼び出された時点で既に 1 人目が否定されている」場合に立てる
  // (handleNo からの再開)。ループ内で候補を切り替えたときも同じ扱いになる。
  const confirmLoop = async (
    start: NameCandidate,
    myGeneration: number,
    followUp = false
  ): Promise<boolean> => {
    let current = start;
    let isFollowUp = followUp;
    // 直前に提示した候補。加算と emit を「候補が切り替わった回だけ」に絞るために持つ
    // (曖昧応答での聞き直しは同じ候補への同じ問いなので提示回数に数えない)。
    // 比較はオブジェクトの同一性で行う — 候補はすべて logRef の要素なので鍵の重複を
    // 気にせず「同じ候補か」を厳密に判定できる。呼び出しごとに null から始まるのは
    // 正しい: 新しい confirmLoop は必ず新しい候補の提示から始まる (nextUnpresented が
    // denied を除くので、一度提示した候補が再訪されることはない)。
    let presented: NameCandidate | null = null;
    // 曖昧応答の聞き直し上限は **候補ごとに数え直す** (別の候補への問いは別の問い)。
    // 持ち越すと 2 人目が 1 回の曖昧応答で打ち切られ、まだ見せていない候補を本人が
    // 確かめられないまま聴取が止まる。
    let unclear = 0;
    while (!isStale(myGeneration)) {
      setCandidate(current.display);
      // ボタンの「はい」が出所と順位を読む先 (§5.4)。**setCandidate と同じ場所で書く** —
      // 離すと、画面に出ている候補と計測に載る候補が食い違いうる。
      presentedRef.current = current;
      setCandidateFollowUp(isFollowUp);
      setState("confirming");
      if (presented !== current) {
        presented = current;
        presentedCountRef.current += 1;
        // D25-5: 逐次確認 (同じ step で 2 人目以降の候補) が実際に提示された通知。
        // 1 人目 (isFollowUp=false) は逐次確認ではないので発火しない。
        if (isFollowUp) onFollowupPresentedRef.current(presentedCountRef.current);
      }
      // display は必ずカタカナ (formatName 出力) なので、そのまま TTS に渡せば日本語として
      // 読める。spokenName の別語彙は不要 (D23)。
      const confirmText = confirmQuestion(current.display, isFollowUp);
      let answer: "yes" | "no" | "unclear";
      // 読み上げは毎周やり直しなので、毎周ここで閉じ直す (曖昧応答の聞き直しも
      // 別の読み上げなので、押せる状態を持ち越さない)。
      setConfirmNarrated(false);
      try {
        answer = await voice.askYesNo(confirmText, {
          onNarrated: (elapsedMs) => {
            if (isStale(myGeneration)) return;
            confirmNarratedMsRef.current = elapsedMs;
            setConfirmNarrated(true);
          },
        });
      } catch {
        return true; // 認識不能 → ボタン待ちに倒す (原則 5)
      }
      if (isStale(myGeneration)) return true;
      if (answer === "yes") {
        generationRef.current += 1; // このセッションを終了させる (handleYes と同じ作法)
        onRespondRef.current("voice");
        onConfirmedRef.current(
          current.display,
          captureInfo("voice", {
            confirmNarratedMs: confirmNarratedMsRef.current ?? undefined,
            candidateSource: current.source,
            candidateRank: ranksRef.current.get(current.key),
          })
        );
        return true;
      }
      if (answer === "no") {
        // 同じものを二度提示しない (D25-5)。**この 1 行がループの停止条件でもある** —
        // 毎周 denied が 1 つ増え log は有限なので、下の continue は必ず有限回で尽きる。
        deniedRef.current.add(current.key);
        // まだ見せていない候補があれば、録り直す前にそれを確かめる (spec §5.2 (a))。
        // **この判定は認識上限 (D25-7) より先に置く** — 逆順にすると、一度も見せて
        // いない候補を抱えたまま退避することになる。
        const next = nextUnpresented();
        if (next) {
          current = next;
          isFollowUp = true;
          unclear = 0;
          continue;
        }
        setCandidate(null);
        if (recognitionsRef.current >= MAX_RECOGNITIONS) return await escalate("max-attempts");
        await voice.speak(TTS_PHRASES.nameRetry);
        return false; // 次のサイクルへ (案内は上で読み上げ済みなので guidance なし)
      }
      unclear += 1;
      if (unclear >= MAX_UNCLEAR) {
        await voice.speak(TTS_PHRASES.useButton);
        return true; // 聴取を止めてボタン待ちにする
      }
      await voice.speak(TTS_PHRASES.askAgain);
    }
    return true;
  };

  // 第 2 経路 (batch STT) を撃ち、Scribe の確定文が届いたら 2 つの書き起こしを LLM に
  // 並べさせる (spec 2026-08-20 §3.5)。**返す promise は決して reject せず、必ず有限時間で
  // settle する** — 呼び出し側に catch を書かせないのが契約で、pending のまま残すと
  // resolving に固着する (その画面には押せるものが 1 つも無く、合図音だけが 850ms ごとに
  // 鳴り続ける = 原則 5 の完全な違反)。
  //
  // **この関数は同期で返り、throw しない。** 呼ぶのは recognizeSingleUtterance の
  // onSpeechEnd (同期) なので、throw すると client.sendAudio / commit に到達せず
  // **Scribe への commit ごと落ちる**。WAV の encode も中の try に入れてある。
  const startEnrichment = (index: number, audio: Float32Array): NameEnrichment => {
    const startedAt = performance.now();
    let scribeSettledAt: number | null = null;
    const controller = new AbortController();

    let resolveRanked: (v: string[]) => void = () => undefined;
    let settled = false;
    const ranked = new Promise<string[]>((r) => {
      resolveRanked = r;
    });
    // resolve は冪等 (abort と timeout と成功が競合しうる)。
    const settle = (v: string[]) => {
      if (settled) return;
      settled = true;
      resolveRanked(v);
    };

    // Scribe の確定文の受け渡し。**null = 撃たずに終わる** (abort)。
    let deliverScribe: (t: string | null) => void = () => undefined;
    let scribeClosed = false;
    const scribeText = new Promise<string | null>((r) => {
      deliverScribe = r;
    });
    const closeScribe = (t: string | null) => {
      if (scribeClosed) return;
      scribeClosed = true;
      deliverScribe(t);
    };

    void (async () => {
      // --- 第 2 経路 (発話終了と同時。Scribe の確定を待たない) ---
      const secondStartedAt = performance.now();
      let second: string | null = null;
      let secondOutcome: NameEnrichOutcome;
      try {
        second = await withTimeout(
          transcribeNameAudio(encodeWavPcm16(audio), controller.signal),
          SECOND_ASR_TIMEOUT_MS,
          SECOND_ASR_TIMEOUT_MESSAGE
        );
        // 打ち切りを先に見る — abort されると fetch は捨てられて null が返るので、
        // 後から見ると「上流が空を返した」と区別できない。
        // **`"empty"` はここから出ない** — `transcribeNameAudio` が空文字と失敗を
        // どちらも null に畳むため (型宣言のコメント参照)。切り分けたくなったら
        // client 側で空文字を別の値にして返す必要がある。
        secondOutcome = controller.signal.aborted ? "aborted" : second ? "ok" : "failed";
      } catch (e) {
        secondOutcome = controller.signal.aborted
          ? "aborted"
          : isTimeoutError(e, SECOND_ASR_TIMEOUT_MESSAGE)
            ? "timeout"
            : "failed";
      }
      // **撃った回は毎回 emit する (D27)。** 実際の emit は Preflight 側で、ここは
      // callback を呼ぶだけ (preflight.* の唯一の emit 点を動かさない、§5.1)。
      onSecondAsrRef.current({
        index,
        ms: Math.round(performance.now() - secondStartedAt),
        outcome: secondOutcome,
        ...(secondOutcome === "ok" && second ? { text: second } : {}),
      });

      // --- 順位付け (Scribe の確定を待って撃つ = D13 の境界そのもの) ---
      const scribe = await scribeText;
      // **撃たない回は emit もしない (§5.3)。** 確定文が無い回 (STT 失敗 / 空) と、
      // 発火前に打ち切られた回はここで終わる。
      if (scribe === null || settled) return;
      const texts = [scribe, ...(second ? [second] : [])];
      const rankStartedAt = performance.now();
      let list: string[] = [];
      let rankOutcome: NameEnrichOutcome;
      try {
        list = await withTimeout(
          rankNameCandidates(texts),
          RANKING_TIMEOUT_MS,
          RANKING_TIMEOUT_MESSAGE
        );
        rankOutcome = controller.signal.aborted ? "aborted" : list.length > 0 ? "ok" : "empty";
      } catch (e) {
        list = [];
        rankOutcome = controller.signal.aborted
          ? "aborted"
          : isTimeoutError(e, RANKING_TIMEOUT_MESSAGE)
            ? "timeout"
            : "failed";
      }
      onRankedRef.current({
        index,
        ms: Math.round(performance.now() - rankStartedAt),
        paths: texts.length,
        returned: list.length,
        ranked: list,
        novel: countNovelReadings(list, texts),
        outcome: rankOutcome,
      });
      settle(list);
      // 何が起きても pending に残さない (callback が throw する経路も含む)。
    })().catch(() => settle([]));

    return {
      index,
      setScribeText: (text: string) => {
        if (scribeClosed || settled) return;
        // 待ち側の締切の基準点はここで確定する (D23)。
        scribeSettledAt = performance.now();
        closeScribe(text);
      },
      ranked,
      // 「順位付けを発火させない」ことは「ranked を settle させる」ことを意味しない。
      // **両方やる** (§3.5 の settle 契約)。
      //
      // **`settle([])` は今日は等価変異である** — 消しても全テストが緑になることを
      // 実測した (実装後レビュー)。`abort()` の呼び出し 6 箇所はすべて直後に early return し、
      // `awaitRankedWithin` へ落ちる経路が存在しないため。**それでも残すのは、
      // await する経路を 1 つ足した瞬間に `resolving` へ固着する**からで、その画面には
      // ボタンが 1 つも無くマイクも止まったまま合図音が鳴り続ける (原則 5 の完全な違反)。
      // 到達可能な観測点が無いのでテストでは固定できない。**この行を消すときは
      // `abort()` の呼び出し全箇所が early return していることを確かめる。**
      abort: () => {
        controller.abort();
        closeScribe(null);
        settle([]);
      },
      // **新しい定数を作らない** — 「順位付けの待ちは判定 (extractName) の待ちより
      // 長引かせない」がこの締切の意味そのものだから。startedAt への fallback は
      // 到達しない前提の防御 (setScribeText を通らない経路はすべて abort 済み)。
      deadlineAt: () => (scribeSettledAt ?? startedAt) + NAME_EXTRACTION_TIMEOUT_MS,
    };
  };

  // 順位付けの結果を締切つきで待つ (D23)。締切は**内部の締切機構自体が壊れたときの
  // 最後の網**で、通常は内側の 3000ms が先に働く (§4.2 の証明)。
  //
  // **この締切も今日は等価変異である** — `return await enrichment.ranked` に置き換えても
  // 全テストが緑になることを実測した (実装後レビュー)。§4.2 (a) の算術どおり内側が常に
  // 先に働くため。**二重の網であることは実測してある** — 内側を 8000ms に膨らませると
  // 2 本落ち、内側 8000ms + この締切の削除で 4 本落ちる (「最悪値 S+5000」「固着しない」を含む)。
  // **単独で殺せる変異が無いのは、内側が正しいかぎりここへ到達しないから**であって、
  // 要らないからではない。`withTimeout` を外す / `onRanked` を async にする、といった
  // 内側を触る変更をするときは、この行が最後の網であることを思い出すこと。
  const awaitRankedWithin = async (enrichment: NameEnrichment): Promise<string[]> => {
    const remaining = Math.max(0, enrichment.deadlineAt() - performance.now());
    try {
      return await withTimeout(enrichment.ranked, remaining, RANK_DEADLINE_MESSAGE);
    } catch {
      return [];
    }
  };

  // 1 回の「案内 → 聴取 → 復唱確認」サイクル。このサイクルで打ち切ってよいかを返す
  // (true = 終了/確定/ボタン待ちへ移行、false = 次のサイクルへ)。myGeneration は
  // 呼び出し元セッションの世代番号 (isStale の判定に使う、上記コメント参照)。
  const runCycle = async (guidance: string | undefined, myGeneration: number): Promise<boolean> => {
    setState("listening");
    // 前サイクルの案内読み上げが終わった印。listening への切り替えと同じレンダーで
    // 畳むことで、合図音が一瞬だけ復活する窓を作らない (speakGuidance のコメント参照)。
    setNarrating(false);
    // 第 2 経路 + 順位付け。**このサイクルのローカル変数として持つ (D20)** — 消費点が
    // すべてこの関数の中にあるので、世代タグも一致判定も要らない。**消費点が runCycle の
    // 外に増えたら、この決定を見直す前に「本当に外から要るのか」を疑う。**
    //
    // **`let enrich: NameEnrichment | null = null` と書いてはいけない** — TypeScript は
    // 入れ子の関数 (onSpeechEnd) の中での代入を外側の絞り込みに反映しないので、代入後も
    // null に絞られたままになり `enrich?.abort()` の型が never になる (実測)。
    // 初期値に型を当てて絞り込みを起こさない形にする。
    let enrich = null as NameEnrichment | null;
    let name: string;
    try {
      // 課題6/Approach A: 聴取中のフィードバック。認識器の発話検出/終了を UI に反映し、
      // 「聞こえてる／今処理してる」を常に見せる (誤りうる partial テキストは出さない)。
      name = await voice.askText(guidance, {
        onSpeechStart: () => {
          if (isStale(myGeneration)) return;
          setErrorMessage(""); // 話し始めたら前回失敗の文言を畳む
          setState("hearing");
        },
        // 発話が終わった瞬間に「確認しています…」へ (Scribe 確定 + LLM 抽出を待たない前倒し)
        onSpeechEnd: (audio) => {
          // 発話の受け渡しは isStale の判定より**前**に行う (spec 2026-07-29 D3)。
          // 打ち切られたサイクルの発話も測定対象で、ここで捨てるとその回だけ標本が欠ける。
          const index = onNameAudioRef.current(audio);
          // **ガードが囲むのは startEnrichment だけ (spec 2026-08-20 §3.5)。**
          // onSpeechEnd は 1 回の askText で 2 回以上来うる (「えーと」→ 800ms 超の沈黙 →
          // 氏名)。冒頭で早期 return すると onNameAudio も 2 回目で呼ばれなくなり、
          // **今日の計測 (WAV 2 本) が変わる** — あれは標本で、index が 2 つ振られること
          // 自体は正しい。**撃つのは最初のセグメントだけ (D2)** で、askText が返すのも
          // 第 1 セグメントの確定文なので、エンジン 1 と 2 が完全に同じ音声区間になる
          // (2 本目で撃つと「エンジン 1 = えーと / エンジン 2 = 氏名」のずれた組になる)。
          if (enrich === null) enrich = startEnrichment(index, audio);
          if (isStale(myGeneration)) {
            enrich.abort();
            return;
          }
          setState("resolving");
        },
      });
    } catch {
      // 確定文が来ない = **順位付けは撃たない (D13)**。第 2 経路の fetch も畳む。
      enrich?.abort();
      if (isStale(myGeneration)) return true;
      const shouldKeepRetrying = await registerFailure();
      return !shouldKeepRetrying;
    }
    if (isStale(myGeneration)) {
      enrich?.abort();
      return true;
    }
    if (!name.trim()) {
      // 短すぎる/不明瞭な発話は空文字で解決することがある。成功扱いにすると
      // candidate が空のまま confirming に入りユーザーが詰む (D12 違反)。
      enrich?.abort(); // 確定文が無いので順位付けは撃たない (D13)
      const shouldKeepRetrying = await registerFailure();
      return !shouldKeepRetrying;
    }
    attemptsRef.current += 1;
    setErrorMessage("");
    setState("resolving");
    // **順位付けの発火はここ (t=S)。** extractName と並行に走らせるのが要点で、
    // await の前に置く (後ろに置くと 2 つの往復が直列になり予算を超える、§3.1)。
    // 待ち側の締切の基準点もこの時刻で確定する (D23)。
    enrich?.setScribeText(name);
    const { display, asrDisplay, verdict } = await resolveName(name);
    if (isStale(myGeneration)) {
      enrich?.abort();
      return true;
    }

    // 「何と聞こえたか」を 1 箇所で確定させる。ここに置くのは**この下で経路が 3 つに
    // 分かれる**ため — 非氏名で弾く枝 (D24) は候補を集める手前で return し、
    // 復唱に進む枝と cands が空で失敗扱いにする枝はさらに先にある。分岐ごとに書くと
    // 必ずどれかが漏れる (当初 logRef 側に置いて、名字だけの回が抜けた)。
    // 優先順は cands の組み立てと同じ llm → asr。両方空なら認識失敗なので触らない
    // (見出しだけが出て中身が空になる)。
    const heard = display || asrDisplay;
    if (heard) setLastHeard(heard);

    // partial の計測 (spec 2026-08-19 §5)。**fail-open の判定より前**に置く —
    // 数えているのは判定器の出力で、こちらが受理したかどうかとは独立だから。
    if (verdict === "partial") partialsRef.current += 1;

    // D24: 氏名かどうかの判定。閾値に達したら判定を降ろし、以降この step では
    // 復唱確認に直行する (fail-open は一方通行 — 誤判定で声だけの経路が詰むのを防ぐ)。
    // attemptsRef はこの上流で既に +1 済みなのでここでは足さない (二重計上防止)。
    const failOpen = rejectionsRef.current >= MAX_REJECTIONS;
    if (!failOpen) {
      // **却下するのは unrelated と refusal だけ (spec 2026-08-19 D2)。**
      // partial は復唱確認へ進める — パイロットの実測では却下 14 発話のうち 10 件が
      // unrelated で大半が誤判定、partial も含めた却下がそのまま「お名前が聞き取れません
      // でした」の回数だった。受理を広げると却下は 2 発話 (本物の非氏名だけ) に減る。
      if (isRejected(verdict)) {
        // **順位付けを待たない (§3.1)。** ここで待つと「お名前が聞き取れませんでした」の
        // 読み上げが 1 往復ぶん遅れる。既に撃ってしまった回は outcome:"aborted" として
        // 残るので、課金したのに捨てた回も後から数えられる (§5.2)。
        enrich?.abort();
        rejectionsRef.current += 1;
        onRejectedRef.current(verdict, attemptsRef.current, rejectionsRef.current);
        const guidance = REJECTION_TEXT[verdict];
        setErrorMessage(guidance);
        // 何と聞こえたかは上で記録済み。**理由ラベルは添えない** — 珍しい姓を
        // unrelated と誤判定した回に「お名前として認識できませんでした」と出すと、
        // 本人の名前を否定する形で残る。
        // state は resolving のまま (spec 2026-07-25 §5.2「新しい表示状態を増やさない」)。
        // voice.speak は聴取を張らないため、ここで listening (「聞いています…」) を出すと
        // 認識器が 1 つも動いていない読み上げ中に話しかけさせ、その発話を丸ごと捨てて
        // しまう。次サイクル冒頭の setState("listening") が実際の聴取と同時に切り替える。
        // 合図音だけは降ろす (speakGuidance) — 表示は resolving のままでも、声に音を
        // 重ねてはいけない (spec 2026-07-27 §1.2)。
        await speakGuidance(guidance);
        if (isStale(myGeneration)) return true;
        return false; // 復唱せず次のサイクルへ (案内は読み上げ済みなので guidance なし)
      }
      // 判定器が当てたので連続カウンタを戻す。閾値到達後 (failOpen) は戻さない。
      // **partial でも戻す (spec 2026-08-19 D2)。** このカウンタは「判定器が連続で
      // 却下した回数」なので、受理された verdict は streak を切る。切らないと
      // unrelated → partial → unrelated で fail-open に入り、
      // preflight.name_rejected.consecutive という payload 名と食い違う。
      if (verdict !== null) rejectionsRef.current = 0;
    }

    // D25-1 / D25-6: この発話から得た表記を集める。**候補は生成しない** —
    // 認識器が実際に出した表記だけを扱うので、実在性は観測で担保される
    // (辞書が要らない理由、spec §2.3)。
    // llm = LLM の読み経由 / asr = Scribe の生テキスト経由。この 2 つは
    // **独立な経路ではない** (LLM は Scribe の出力を見ている) ので、一致を
    // 確信度としては使わない。不一致のとき 2 つ目の候補が手に入るのが利点。
    //
    // **組み立ては純関数 (buildNameCandidates) が持つ (spec 2026-08-20 §3.4)。**
    // `ranked` は順位付け (第 2 経路を含む 2 つの書き起こしを LLM に並べさせた出力) で、
    // **並びの先頭に入るだけ** — `display` / `asrDisplay` を押し出さない (D18)。
    // 落ちたら空配列になり、出力は現行の `cands` と 1 ビットも同じになる (原則 5)。
    const ranked = enrich ? await awaitRankedWithin(enrich) : [];
    if (isStale(myGeneration)) return true;
    const { all, fresh } = buildNameCandidates({
      ranked,
      display,
      asrDisplay,
      denied: deniedRef.current,
    });
    if (all.length === 0) {
      // 表示に落とせるカナが残らなかった (raw 漢字のみ + LLM 失敗 など、D23)。
      // 認識失敗扱いにして自動再リスニング → 上限到達で手動リトライ/キーボード誘導へ。
      //
      // **enrich を畳むのは他の失敗枝と揃えるため** — ここは `enrich?.abort()` を
      // 呼ばない唯一の registerFailure 呼び出し点だった。畳まないと第 2 経路の往復が
      // 宙に浮き、段 3 が `outcome:"aborted"` を分けた目的 (課金して捨てた回を数える)
      // がこの経路でだけ達成されない。
      enrich?.abort();
      // **`counted: true`**: この経路は上の `attemptsRef.current += 1` を通ってきている
      // ので、registerFailure 側でもう一度足すと 1 発話で attempts が 2 進む
      // (`preflight.name_captured.attempts` は「通算値」と定義されている軸)。
      const shouldKeepRetrying = await registerFailure({ counted: true });
      return !shouldKeepRetrying;
    }

    // 「候補が 1 件以上出た」= このサイクルは成功。**この 2 行は必ず一緒に置く。**
    //
    // `failuresRef` のリセットは `registerFailure` を呼びうる全経路 (askText の catch /
    // 空の確定文 / ここ) より**すべて後ろ**でなければならない。askText の解決直後
    // (`attemptsRef` の隣) に戻すと、3 つ目が「自分でリセットしてから +1 する」形になり
    // **`MAX_CONSECUTIVE_FAILURES` に永久に届かない**。もう一方の網も効かない —
    // `MAX_RECOGNITIONS` との比較は confirmLoop の「いいえ」と handleNo の 2 箇所だけで、
    // どちらもこの経路上に無い。結果、画面は listening/hearing/resolving を回り続けて
    // `retry-prompt` に一度も入らず、**ボタンが 1 つも描画されないまま**
    // WAV のアップロードと第 2 経路の往復が無制限に走る (原則 5 と原則 6 の同時違反)。
    //
    // **却下 (isRejected) の枝でリセットしてはいけない。** 却下とカナ無しが交互に来ると
    // streak が 1 と 0 を往復して同じループが再生産される (温度 0 でも判定は揺れる)。
    recognitionsRef.current += 1;
    failuresRef.current = 0;
    if (fresh.length === 0) {
      // 今回も既に否定済みのものしか出ていない = 足踏み。
      //
      // ただし **鍵は同じで表記だけが違う** なら、それは足踏みではなく「音では
      // 決まらない差」(D25-4)。本人は読み上げでハヅキとハズキを聞き分けられない
      // ので「いいえ」と答えるしかなく、言い直しても同じ確率で同じ表記が出る
      // (spec §1.1 欠陥 3 の典型経路がここに落ちる)。否定された表記をもう一度
      // 出すことになるが、**差を目で見せるのはこれが初めて**なので同じ問いの
      // 繰り返しにはならない。
      // 突き合わせるのは `all` (切っていない並び) — 否定済みの表記も残っているので、
      // 「鍵は同じで表記だけ違う」組を取りこぼさない (D18 / §3.4 の規則 4)。
      const pair = findSpellingPair(all);
      if (pair) return await resolveSpelling(pair.first, pair.second, myGeneration);
      // 表記まで同じなら言い直しても変わらないので音声ループから降ろす
      // (spec §1.1 欠陥 1 の本質的な解決)。
      return await escalate("no-progress");
    }
    const top = fresh[0];
    // **prior は logRef へ積む前に取る。** 順序を逆にすると prior が必ず自分自身を
    // 見つけ、下の「動いたら不変条件が壊れている」枝が毎サイクル発火する。
    const prior = logRef.current.find((a) => a.key === top.key);
    fresh.forEach((c, i) => {
      // 積むのは fresh だけ (D18)。all を積むと nextUnpresented が上限を無視して
      // 同じサイクルで 3 件目以降を出し、復唱が 3 往復以上になる。
      if (!logRef.current.some((a) => a.key === c.key)) logRef.current.push(c);
      // 提示の順序 = その回の fresh での位置 (1 始まり、§5.4)。積むのと同じ場所で
      // 覚える — 別の場所に書くと、候補を足したときに順位だけが古くなる。
      ranksRef.current.set(c.key, i + 1);
    });
    if (prior) {
      // **D25-5 (逐次確認) を入れた後、この枝は UI からは到達しなくなっている。**
      // 「いいえ」を受けるたびに log の未提示分を出し切るので (confirmLoop / handleNo)、
      // 次のサイクルが始まる時点では log のすべての鍵が denied に入っており、
      // fresh に既出の鍵が残ることがない。spec §5.1 (b) が定義している枝なので
      // 残してあるが、**ここが動いたら「出し切る」不変条件が壊れている**という意味に
      // なる (逐次確認を止める / log に提示されない候補を積む変更を入れたときなど)。
      if (prior.display !== top.display) {
        // 鍵は同じで表記だけが違う = 音では決まらない差 (D25-4)。
        return await resolveSpelling(prior, top, myGeneration);
      }
      // 表記まで一致 = 言い直しても変わらない。この経路では決まらない (D25-3)。
      return await escalate("same-result");
    }

    return await confirmLoop(top, myGeneration);
  };

  useEffect(() => {
    const myGeneration = ++generationRef.current;
    void (async () => {
      // 初回だけ案内を読み上げ、2 回目以降は直前に読み上げ済みなので聴取のみ
      let guidance: string | undefined = TTS_PHRASES.namePrompt;
      while (!isStale(myGeneration)) {
        const finished = await runCycle(guidance, myGeneration);
        if (finished) return;
        guidance = undefined;
      }
    })();
    return () => {
      generationRef.current += 1; // このセッションを無効化する
      voice.cancel();
    };
    // voice は usePreflightVoice の返り値そのものが useMemo で安定しているため
    // (SpeakerCheck と同じ理由)、deps は [voice] だけで正しい。runCycle/registerFailure/
    // isStale は毎レンダー再生成される非メモ化ヘルパーで、中で読むのは voice
    // (deps 済み) と ref (attemptsRef/failuresRef/generationRef/onConfirmedRef/
    // onRespondRef、常に最新値) だけだが、これらを deps に足すと (useCallback 化
    // しない限り) 逆に毎レンダーで effect が再発火し、聴取が丸ごと再起動してしまう
    // (SpeakerCheck の playTest と同種の判断のため useCallback 化はこのタスクの
    // 対象外とする)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice]);

  const handleYes = () => {
    if (!candidate) return;
    generationRef.current += 1;
    voice.cancel();
    onRespondRef.current("button");
    // ボタンの「はい」も復唱を聞いた上での確定なので、同じ待ち時間を載せる。
    // 出所と順位は presentedRef から読む — 画面の文字列 (candidate) だけでは
    // どの供給元の何番目だったかが分からず、声とボタンで payload が食い違う (§5.4)。
    const presented = presentedRef.current;
    onConfirmedRef.current(
      candidate,
      captureInfo("voice", {
        confirmNarratedMs: confirmNarratedMsRef.current ?? undefined,
        candidateSource: presented?.source,
        candidateRank: presented ? ranksRef.current.get(presented.key) : undefined,
      })
    );
  };

  const handleNo = () => {
    // onRespond はここでは呼ばない: onRespond は preflight.response {step, method}
    // (spec 2026-07-19 §6 / D16) の集計元で、「この step を音声/ボタンのどちらで
    // 完了したか」を測る指標であり、「いいえ」は再録のトリガーであって完了ではない。
    // ここで呼ぶと 1 step につき複数回 emit されて計測が壊れる (完了は handleYes と
    // confirmLoop の yes 分岐だけが呼ぶ)。SpeakerCheck の onHeard (最終確認でのみ発火)
    // と同じ規約。
    //
    // 新しいセッションを起こしてから (これで前のセッションは isStale 経由で自動的に
    // 無効化される) 保留中の askYesNo を打ち切る。打ち切らずに
    // voice.speak(TTS_PHRASES.nameRetry) の完了を待ってから次のサイクルに入ると、待ち時間中は
    // 古い askYesNo がまだ生きたままになり、たまたまその間に拾われた発話が「yes」と
    // 誤判定された場合、ユーザーが今しがた「いいえ」を押して拒否したはずの名前で
    // 確定してしまう (handleYes と同じく cancel-first の作法、usePreflightVoice の
    // 「同時に生きている recognizer は高々 1 つ」不変条件に合わせる)。
    const myGeneration = ++generationRef.current;
    voice.cancel();
    // 否定された鍵は confirmLoop の音声「いいえ」と同じく記録する (D25-5)。candidate は
    // クリック時点のレンダーの値なので、いま画面に出ていた候補で正しい。
    if (candidate) deniedRef.current.add(matchKey(candidate));
    // 声の「いいえ」と同じ経路を通す — まだ見せていない候補があればそれを先に確かめる
    // (D25-5)。ここで分岐が食い違うと、同じ「いいえ」が手段によって別の結果になる。
    const next = nextUnpresented();
    setCandidate(null);
    setState("listening");
    void (async () => {
      if (next) {
        // confirmLoop は同期区間で candidate / state を書き直すので、上の listening は
        // 描画されない (click handler の同じ tick でまとめて反映される)。
        if (await confirmLoop(next, myGeneration, true)) return;
        // false = 録り直しへ。再録の案内は confirmLoop が読み上げ済み。
      } else {
        if (recognitionsRef.current >= MAX_RECOGNITIONS) {
          await escalate("max-attempts");
          return;
        }
        await voice.speak(TTS_PHRASES.nameRetry);
      }
      while (!isStale(myGeneration)) {
        if (await runCycle(undefined, myGeneration)) return;
      }
    })();
  };

  // D21: テキストで氏名を確定する最後の手段。音声の attempts をそのまま引き継ぐ
  // (氏名認識に何回失敗した末のキーボード確定かを name_captured.attempts で残す)。
  // 入力もカタカナに正規化して漢字は除去する (D23。表示はカタカナのみを守る)。
  const normalizedTextName = formatName(textValue.trim(), "");
  const handleTextSubmit = () => {
    if (!normalizedTextName) return;
    generationRef.current += 1; // 進行中の聴取を無効化する (handleYes と同じ cancel-first の作法)
    voice.cancel();
    onRespondRef.current("keyboard");
    onConfirmedRef.current(normalizedTextName, captureInfo("keyboard"));
  };

  const handleManualRetry = () => {
    const myGeneration = ++generationRef.current;
    failuresRef.current = 0;
    rejectionsRef.current = 0; // 明示的な仕切り直しなので判定も戻す (D24-3)
    // 本人が仕切り直しを選んだ以上、こちらの都合の記憶は捨てる (D25-3 / spec §5.3)。
    // これを残すと、リセット後の 1 発話目が「既出」扱いで即座に退避へ戻ってしまう。
    logRef.current = [];
    // 提示の順序と「いま復唱している候補」も logRef と一緒に捨てる (§5.4)。残すと、
    // 仕切り直し後の確定に前の並びの順位が貼り付く (段 1 で confirmNarratedMs が
    // キーボード確定に貼り付いたのと同じ形)。
    ranksRef.current = new Map();
    presentedRef.current = null;
    setLastHeard(null); // 仕切り直しの意味と一致させる (聞き取りの記憶も捨てる)
    deniedRef.current = new Set();
    recognitionsRef.current = 0;
    presentedCountRef.current = 0; // 提示した候補の通算もこの step の仕切り直しでリセット
    setErrorMessage("");
    setSpellingChoice(null); // 表記の 2 択も仕切り直しの対象 (D25-4)
    setShowTextInput(false);
    setTextValue("");
    void (async () => {
      while (!isStale(myGeneration)) {
        if (await runCycle(TTS_PHRASES.namePrompt, myGeneration)) return;
      }
    })();
  };

  // 聴取に関わる 3 状態 (待機 / 発話検出中 / 確認中)。マイクのインジケータと
  // 聞き取り履歴が同じ区間に出る。
  const isListening = state === "listening" || state === "hearing" || state === "resolving";

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-slate-700">お名前をフルネームでお話しください。</p>
      {isListening && (
        // 課題6/Approach A: 聴取中フィードバック。押下操作はない (spec D12)。
        // listening=待機 / hearing=声を検出 (波紋) / resolving=確認中 (スピナー) で
        // 見た目と文言を切り替え、「聞こえてる／今処理してる」を常に見せる。
        <div className="flex flex-col items-center gap-1.5">
          <span aria-hidden className="relative flex items-center justify-center w-12 h-12">
            {state === "hearing" && (
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-60 animate-ping" />
            )}
            {state === "resolving" && (
              <span className="absolute inset-0 rounded-full border-2 border-slate-200 border-t-indigo-500 animate-spin" />
            )}
            <span
              className={
                "relative w-11 h-11 rounded-full border flex items-center justify-center text-lg transition-colors " +
                (state === "hearing"
                  ? "border-emerald-400 bg-emerald-100 text-emerald-700"
                  : state === "resolving"
                    ? "border-indigo-200 bg-indigo-50 text-indigo-500"
                    : "border-emerald-200 bg-emerald-50 text-emerald-600 animate-pulse")
              }
            >
              🎙
            </span>
          </span>
          {/* text-xs = 12px。HIG の最小フォント 11pt を下回らないようにする */}
          <span role="status" className="text-xs font-medium text-slate-500">
            {state === "hearing"
              ? "お名前を聞き取っています…"
              : state === "resolving"
                ? "お名前を確認しています…"
                : "聞いています…"}
          </span>
        </div>
      )}
      {isListening && lastHeard !== null && (
        // 言い直しの最中だけ出す。実データでは同じ人の氏名が 7 通りに割れ、誤りは
        // 語頭 1 音に集中しているので (docs/measurements/2026-07-28-name-recognition-accuracy.md)、
        // 話し始める直前に何と聞こえたかが見えると直す場所が分かる。
        //
        // **復唱確認中には出さない** — 確認すべき候補は 1 つなのに画面に氏名が 2 つ並ぶと、
        // どちらに はい/いいえ を答えているのか分からなくなる。
        //
        // 出すのは最新の 1 件だけ (履歴にしない、lastHeard のコメント参照)。
        <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-col gap-1.5">
          {/* text-xs = 12px。HIG の最小フォント 11pt を下回らないようにする */}
          <p className="text-xs font-semibold text-slate-500">{HEARD_TITLE_TEXT}</p>
          <p className="text-sm font-semibold text-slate-600">{lastHeard}</p>
          <p className="text-xs text-slate-500">{HEARD_HINT_TEXT}</p>
        </div>
      )}
      {state === "confirming" && candidate !== null && (
        <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-col gap-2">
          {/* 画面の問いは読み上げと同じ文字列にする (聞き逃した人が読めるように) */}
          <p className="text-sm text-slate-900">{confirmQuestion(candidate, candidateFollowUp)}</p>
          {/* flex-1 で幅は足りるので、高さだけ min-h-[44px] で HIG のタップ領域 44pt に届かせる */}
          <div className="flex gap-2">
            {/* 読み上げが鳴り終わるまで押せない (2026-08-20、confirmNarrated のコメント参照)。
                隠すのではなく灰色で置くのは、後から現れると視線が動くため — 押せるように
                なることが見えていれば待てる。判定は 1 箇所 (disabled) だけに持たせる:
                onClick 側にも同じ条件を書くと、片方の変異がもう片方に隠れる */}
            <button
              type="button"
              onClick={handleYes}
              disabled={!confirmNarrated}
              className="flex-1 min-h-[44px] py-2 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:hover:bg-slate-200"
            >
              はい
            </button>
            <button
              type="button"
              onClick={handleNo}
              className="flex-1 min-h-[44px] py-2 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
            >
              いいえ
            </button>
          </div>
        </div>
      )}
      {state === "spelling" && spellingChoice !== null && (
        // D25-4: 音では決まらない表記の差。読み上げでは伝わらないので、目で見て選べる形に
        // 出す。同時に音声でも聞いており (「いいえ」= もう一方)、どちらでも決められる。
        <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-col gap-2">
          <p className="text-sm text-slate-900">{SPELLING_PROMPT_TEXT}</p>
          <div className="flex gap-2">
            {(["first", "second"] as const).map((which) => (
              <button
                key={which}
                type="button"
                onClick={() => commitSpelling(which, "button")}
                className="flex-1 min-h-[44px] py-2 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
              >
                {which === "first" ? spellingChoice.first.display : spellingChoice.second.display}
              </button>
            ))}
          </div>
          {/* 文字リンク型のボタンは min-h-[44px] でタップ領域だけ 44pt に広げる
              (文字色・下線は据え置き — 最後の手段を主動線と競合させない)。
              px-4 は元のまま — 縮めると短いラベルのボタンが横 44pt を割りかねない */}
          <button
            type="button"
            onClick={handleSpellingGiveUp}
            className="self-center inline-flex items-center min-h-[44px] px-4 rounded-full text-xs font-semibold text-slate-500 hover:text-slate-700 hover:underline"
          >
            どちらでもありません
          </button>
        </div>
      )}
      {errorMessage && state !== "confirming" && (
        <p className="text-xs text-amber-700 text-center">{errorMessage}</p>
      )}
      {state === "retry-prompt" && (
        <div className="flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={handleManualRetry}
            className="self-center inline-flex items-center min-h-[44px] px-4 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
          >
            もう一度試す
          </button>
          {!showTextInput ? (
            <button
              type="button"
              onClick={() => setShowTextInput(true)}
              className="self-center inline-flex items-center min-h-[44px] px-4 rounded-full text-xs font-semibold text-slate-500 hover:text-slate-700 hover:underline"
            >
              キーボードで入力
            </button>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleTextSubmit();
              }}
              className="w-full flex flex-col gap-2"
            >
              <input
                type="text"
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                aria-label="お名前"
                placeholder="お名前 (カタカナ)"
                /* text-base = 16px: iOS Safari は 16px 未満の入力欄に focus すると画面を
                   強制ズームする。ここは音声で詰まった人が辿り着く最後の手段 (D21) なので、
                   まさにリカバリが要る人がその状態に落ちる */
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-base min-h-[44px]"
              />
              {/* 「決定」は 2 文字しかなく横幅が最も細い。px-4 (元のまま) で 12px×2 + 16px×2
                  = 約 56px を確保する — px-3 に縮めると約 48px となり 44pt の余裕が 4px しか
                  残らず、フォントの差で下回りうる */}
              <button
                type="submit"
                disabled={!normalizedTextName}
                className="self-center inline-flex items-center min-h-[44px] px-4 rounded-full text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                決定
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
